import React, { useEffect, useState } from 'react';
import { Edit3, MapPinned, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiDelete, apiGet, apiPatch, apiPost } from '../../lib/api';

const inputCls = 'w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rozops-blue/20 focus:border-rozops-blue';
const emptyForm = { route_name: '', area: '', sequence: 0 };

export default function RoutesPage() {
  const [routes, setRoutes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = () => apiGet('/merchant/routes').then(r => setRoutes(r.data || [])).catch(e => toast.error(e.message));
  useEffect(() => { load(); }, []);

  const reset = () => {
    setShowForm(false);
    setEditing(null);
    setForm(emptyForm);
    setSaving(false);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (route) => {
    setEditing(route);
    setForm({
      route_name: route.route_name || '',
      area: route.area || '',
      sequence: route.sequence ?? 0,
    });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        route_name: form.route_name.trim(),
        area: form.area.trim() || null,
        sequence: Number(form.sequence || 0),
      };
      if (editing) {
        await apiPatch(`/merchant/routes/${editing.id}`, payload);
        toast.success('Route updated');
      } else {
        await apiPost('/merchant/routes', payload);
        toast.success('Route created');
      }
      reset();
      await load();
    } catch (err) {
      toast.error(err.message);
      setSaving(false);
    }
  };

  const remove = async (route) => {
    if (!window.confirm(`Deactivate ${route.route_name}?`)) return;
    setDeletingId(route.id);
    try {
      await apiDelete(`/merchant/routes/${route.id}`);
      toast.success('Route deactivated');
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="routes-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading font-extrabold text-3xl text-rozops-navy tracking-tight">Routes</h1>
        <button onClick={openCreate} className="bg-rozops-blue text-white rounded-xl px-4 py-2.5 font-semibold shadow-soft inline-flex items-center gap-2" data-testid="add-route-btn">
          <Plus className="w-4 h-4" /> Add route
        </button>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {routes.length === 0 && <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center text-rozops-text-muted col-span-full">No routes yet. Create delivery areas or route sequences for your team.</div>}
        {routes.map(route => (
          <div key={route.id} className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5" data-testid={`route-${route.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="w-10 h-10 rounded-xl bg-rozops-blue-light text-rozops-blue grid place-items-center"><MapPinned className="w-5 h-5" /></div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => openEdit(route)} title="Edit route" aria-label={`Edit ${route.route_name}`} className="w-9 h-9 rounded-lg text-rozops-text-muted hover:bg-gray-50 hover:text-rozops-blue grid place-items-center">
                  <Edit3 className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => remove(route)} disabled={deletingId === route.id} title="Deactivate route" aria-label={`Deactivate ${route.route_name}`} className="w-9 h-9 rounded-lg text-rose-500 hover:bg-rose-50 disabled:opacity-50 grid place-items-center">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="mt-4 font-heading font-bold text-lg text-rozops-navy break-words">{route.route_name}</div>
            <div className="text-sm text-rozops-text-muted break-words">{route.area || 'No area set'}</div>
            <div className="mt-3 text-xs font-bold uppercase tracking-normal text-rozops-text-muted">Sequence {route.sequence ?? 0}</div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={reset}>
          <div className="bg-white rounded-2xl shadow-medium p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading font-bold text-xl text-rozops-navy mb-4">{editing ? 'Edit route' : 'Add route'}</h2>
            <form onSubmit={save} className="space-y-3">
              <input required placeholder="Route name" className={inputCls} value={form.route_name} onChange={(e) => setForm({ ...form, route_name: e.target.value })} data-testid="route-name" />
              <input placeholder="Area" className={inputCls} value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} data-testid="route-area" />
              <input type="number" step="1" placeholder="Sequence" className={inputCls} value={form.sequence} onChange={(e) => setForm({ ...form, sequence: e.target.value })} data-testid="route-sequence" />
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={reset} className="px-4 py-2 rounded-xl text-rozops-text-muted">Cancel</button>
                <button type="submit" disabled={saving} className="bg-rozops-blue text-white rounded-xl px-4 py-2 font-semibold disabled:opacity-60" data-testid="route-submit">{saving ? 'Saving...' : editing ? 'Save changes' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
